import { Box, Button, ButtonGroup, Flex, IconButton, Input, Popover, PopoverBody, PopoverContent, PopoverTrigger, Spinner, Text, Tooltip, useDisclosure } from "@chakra-ui/react";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import Underline from "@tiptap/extension-underline";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import DOMPurify from "dompurify";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  FaBold,
  FaImage,
  FaItalic,
  FaLink,
  FaListOl,
  FaListUl,
  FaQuoteRight,
  FaRedo,
  FaStrikethrough,
  FaUnderline,
  FaUndo,
} from "react-icons/fa";
import { crm } from "../../theme";
import { ATTACHMENT_ATTR, AttachmentImage, acquire, release } from "./attachments";

// Mesmo visual no editor e na leitura (o HTML vem sanitizado do backend: RichTextSanitizer).
const contentStyles = {
  fontSize: "sm",
  lineHeight: 1.65,
  color: crm.text,
  wordBreak: "break-word",
  "p": { my: "6px" },
  "h1, h2, h3": { fontWeight: 700, mt: "16px", mb: "6px", lineHeight: 1.3 },
  h1: { fontSize: "xl" },
  h2: { fontSize: "lg" },
  h3: { fontSize: "md" },
  "ul, ol": { pl: "22px", my: "6px" },
  ul: { listStyleType: "disc" },
  ol: { listStyleType: "decimal" },
  "ul ul": { listStyleType: "circle" },
  li: { my: "2px" },
  "li > p": { my: 0 },
  a: { color: crm.brand, textDecoration: "underline" },
  [`img[${ATTACHMENT_ATTR}]`]: { display: "block", maxW: "100%", my: "8px", borderRadius: "6px", border: "1px solid", borderColor: crm.border },
  blockquote: { borderLeft: "3px solid", borderColor: crm.borderStrong, pl: "12px", color: crm.textMuted, my: "8px" },
  code: { bg: crm.columnBg, px: "4px", borderRadius: "4px", fontSize: "0.9em" },
  pre: { bg: crm.columnBg, p: "10px", borderRadius: crm.radiusSm, overflowX: "auto", "code": { bg: "transparent", p: 0 } },
  hr: { my: "14px", borderColor: crm.border },
  table: { borderCollapse: "collapse", my: "8px", w: "100%" },
  "th, td": { border: "1px solid", borderColor: crm.border, px: "8px", py: "4px", textAlign: "left", verticalAlign: "top" },
  th: { bg: crm.subtle, fontWeight: 600 },
};

const safeUrl = (value: string) => {
  const url = value.trim();
  if (!url) return null;
  // "exemplo.com:8080" não é esquema (dígito depois do ":"); javascript:, ftp: etc. são recusados
  const withScheme = /^(https?:\/\/|mailto:)/i.test(url) ? url : /^[a-z][a-z0-9+.-]*:(?!\d)/i.test(url) ? null : `https://${url}`;
  return withScheme && /^(https?:\/\/[^\s/]+|mailto:\S+)/i.test(withScheme) ? withScheme : null;
};

const hasPastedText = (data: DataTransfer | null) => {
  if (!data) return false;
  if (data.getData("text/plain").trim()) return true;
  const html = data.getData("text/html");
  return !!html && !!new DOMParser().parseFromString(html, "text/html").body.textContent?.trim();
};

export const RichTextView = ({ html, empty = "Sem conteúdo." }: { html?: string | null; empty?: string }) => {
  // Segunda barreira: o backend já sanitiza, mas o navegador não depende só dele
  const clean = useMemo(() => (html ? DOMPurify.sanitize(html, { ADD_ATTR: ["target", ATTACHMENT_ATTR] }) : ""), [html]);
  const box = useRef<HTMLDivElement>(null);

  // Imagens de anexo: busca com o token e põe o object URL no src
  useEffect(() => {
    const images = Array.from(box.current?.querySelectorAll<HTMLImageElement>(`img[${ATTACHMENT_ATTR}]`) ?? []);
    const ids = images.map((img) => img.getAttribute(ATTACHMENT_ATTR)!);
    let alive = true;
    images.forEach((img, i) => {
      img.alt = "carregando imagem…";
      acquire(ids[i]).then((url) => { if (alive) img.src = url; }).catch(() => { if (alive) img.alt = "[imagem indisponível]"; });
    });
    return () => {
      alive = false;
      ids.forEach(release);
    };
  }, [clean]);

  return clean ? (
    <Box ref={box} sx={contentStyles} dangerouslySetInnerHTML={{ __html: clean }} />
  ) : (
    <Box fontSize="sm" color={crm.textFaint}>{empty}</Box>
  );
};

interface RichTextEditorProps {
  value?: string | null;
  onChange: (html: string) => void;
  placeholder?: string;
  minH?: string;
  /** Sobe a imagem como anexo e devolve o id (null = falhou, já avisado). Sem isto não há botão de imagem. */
  onUploadImage?: (file: File) => Promise<string | null>;
}

export const RichTextEditor = ({ value, onChange, minH = "220px", onUploadImage }: RichTextEditorProps) => {
  const [uploading, setUploading] = useState(0);
  const uploadRef = useRef(onUploadImage);
  uploadRef.current = onUploadImage;
  const editorRef = useRef<Editor | null>(null);

  const insertImages = async (files: File[]) => {
    const upload = uploadRef.current;
    const images = files.filter((file) => file.type.startsWith("image/"));
    if (!upload || images.length === 0) return false;
    setUploading((count) => count + images.length);
    for (const file of images) {
      // Editor fechado no meio: para de enviar o resto
      if (!editorRef.current || editorRef.current.isDestroyed) {
        setUploading((count) => count - 1);
        continue;
      }
      try {
        const id = await upload(file);
        const editor = editorRef.current;
        if (id && editor && !editor.isDestroyed) editor.chain().focus().insertContent({ type: AttachmentImage.name, attrs: { attachmentId: id } }).run();
      } finally {
        setUploading((count) => count - 1);
      }
    }
    return true;
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      // Sem isto o ProseMirror descarta tabelas (importadas do Notion) ao abrir a edição
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      Link.configure({ openOnClick: false, autolink: true, protocols: ["mailto"], HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      AttachmentImage,
    ],
    editorProps: {
      // Colar/arrastar print vira anexo + imagem no texto
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files ?? []);
        if (!uploadRef.current || !files.some((file) => file.type.startsWith("image/"))) return false;
        // Excel/Word mandam o texto junto com um PNG de prévia: com texto, cola o texto
        if (hasPastedText(event.clipboardData)) return false;
        insertImages(files);
        return true;
      },
      handleDrop: (_view, event) => {
        const files = Array.from((event as DragEvent).dataTransfer?.files ?? []);
        if (!uploadRef.current || !files.some((file) => file.type.startsWith("image/"))) return false;
        event.preventDefault();
        insertImages(files);
        return true;
      },
    },
    content: value ?? "",
    onUpdate: ({ editor: current }) => onChange(current.isEmpty ? "" : current.getHTML()),
  });

  editorRef.current = editor;

  // Troca de procedimento sem remontar: só repõe se o valor externo for outro
  useEffect(() => {
    if (!editor) return;
    const next = value ?? "";
    if (next !== (editor.isEmpty ? "" : editor.getHTML())) editor.commands.setContent(next, false);
  }, [editor, value]);

  return (
    <Box border="1px solid" borderColor={crm.borderStrong} borderRadius={crm.radiusSm} bg={crm.surface} _focusWithin={{ borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` }}>
      {editor && <Toolbar editor={editor} onPickImages={onUploadImage ? insertImages : undefined} uploading={uploading > 0} />}
      <Box
        px="12px"
        py="8px"
        sx={{ ...contentStyles, ".ProseMirror": { minH, outline: "none" }, ".ProseMirror > *:first-of-type": { mt: 0 } }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

const Toolbar = ({ editor, onPickImages, uploading }: { editor: Editor; onPickImages?: (files: File[]) => void; uploading?: boolean }) => {
  const chain = () => editor.chain().focus();
  const imageInput = useRef<HTMLInputElement>(null);
  return (
    <Flex gap="4px" p="6px" borderBottom="1px solid" borderColor={crm.border} bg={crm.subtle} wrap="wrap" borderTopRadius={crm.radiusSm}>
      <ButtonGroup size="xs" isAttached variant="ghost">
        <Tool label="Negrito" active={editor.isActive("bold")} onClick={() => chain().toggleBold().run()} icon={<FaBold />} />
        <Tool label="Itálico" active={editor.isActive("italic")} onClick={() => chain().toggleItalic().run()} icon={<FaItalic />} />
        <Tool label="Sublinhado" active={editor.isActive("underline")} onClick={() => chain().toggleUnderline().run()} icon={<FaUnderline />} />
        <Tool label="Tachado" active={editor.isActive("strike")} onClick={() => chain().toggleStrike().run()} icon={<FaStrikethrough />} />
      </ButtonGroup>
      <ButtonGroup size="xs" isAttached variant="ghost">
        {([1, 2, 3] as const).map((level) => (
          <Tooltip key={level} label={`Título ${level}`} openDelay={400}>
            <Button aria-label={`Título ${level}`} aria-pressed={editor.isActive("heading", { level })} bg={editor.isActive("heading", { level }) ? crm.brandSoft : undefined} color={editor.isActive("heading", { level }) ? crm.brand : crm.textMuted} onClick={() => chain().toggleHeading({ level }).run()}>
              H{level}
            </Button>
          </Tooltip>
        ))}
      </ButtonGroup>
      <ButtonGroup size="xs" isAttached variant="ghost">
        <Tool label="Lista" active={editor.isActive("bulletList")} onClick={() => chain().toggleBulletList().run()} icon={<FaListUl />} />
        <Tool label="Lista numerada" active={editor.isActive("orderedList")} onClick={() => chain().toggleOrderedList().run()} icon={<FaListOl />} />
        <Tool label="Citação" active={editor.isActive("blockquote")} onClick={() => chain().toggleBlockquote().run()} icon={<FaQuoteRight />} />
        <LinkTool editor={editor} />
        {onPickImages && <Tool label="Imagem (também dá para colar ou arrastar)" onClick={() => imageInput.current?.click()} icon={uploading ? <Spinner size="xs" /> : <FaImage />} disabled={uploading} />}
      </ButtonGroup>
      {onPickImages && (
        <input ref={imageInput} type="file" accept="image/*" multiple hidden onChange={(e) => { onPickImages(Array.from(e.target.files ?? [])); e.target.value = ""; }} />
      )}
      <ButtonGroup size="xs" isAttached variant="ghost" ml="auto">
        <Tool label="Desfazer" onClick={() => chain().undo().run()} icon={<FaUndo />} disabled={!editor.can().undo()} />
        <Tool label="Refazer" onClick={() => chain().redo().run()} icon={<FaRedo />} disabled={!editor.can().redo()} />
      </ButtonGroup>
    </Flex>
  );
};

const Tool = ({ label, icon, onClick, active, disabled }: { label: string; icon: ReactNode; onClick: () => void; active?: boolean; disabled?: boolean }) => (
  <Tooltip label={label} openDelay={400}>
    <IconButton aria-label={label} aria-pressed={active} icon={<>{icon}</>} onClick={onClick} isDisabled={disabled} bg={active ? crm.brandSoft : undefined} color={active ? crm.brand : crm.textMuted} />
  </Tooltip>
);

const LinkTool = ({ editor }: { editor: Editor }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [url, setUrl] = useState("");
  const [invalid, setInvalid] = useState(false);
  const open = () => {
    setUrl(editor.getAttributes("link").href ?? "");
    setInvalid(false);
    onOpen();
  };
  const apply = () => {
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      onClose();
      return;
    }
    const href = safeUrl(url);
    // Endereço inválido não apaga o link que já existe
    if (!href) {
      setInvalid(true);
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
    onClose();
  };
  return (
    <Popover isOpen={isOpen} onOpen={open} onClose={onClose} placement="bottom-start">
      <PopoverTrigger>
        <IconButton aria-label="Link" aria-pressed={editor.isActive("link")} icon={<FaLink />} bg={editor.isActive("link") ? crm.brandSoft : undefined} color={editor.isActive("link") ? crm.brand : crm.textMuted} />
      </PopoverTrigger>
      <PopoverContent w="320px">
        <PopoverBody>
          <Flex gap="6px">
            <Input size="sm" autoFocus value={url} placeholder="https://..." onChange={(e) => { setUrl(e.target.value); setInvalid(false); }} isInvalid={invalid} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }} />
            <Button size="sm" colorScheme="blue" onClick={apply}>{url.trim() ? "Aplicar" : "Remover"}</Button>
          </Flex>
          {invalid && <Text fontSize="xs" color="red.500" mt="6px">Use um endereço http(s) ou mailto.</Text>}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
